import { useDebouncedState } from "@mantine/hooks";
import { createContext, useContext, ReactNode, useState } from "react";
import { Outlet } from "react-router-dom";
import { GetEventDetailQuery } from "src/generated/graphql";

type ILocationService =
  | NonNullable<GetEventDetailQuery["meetingOne"]>["service"]
  | null;

type IPrivateSession =
  | NonNullable<GetEventDetailQuery["meetingOne"]>["privateSession"]
  | null;

interface ScheduleEventContextType {
  searchedLocValue: string;
  setSearchedLocValue: (e: string) => void;
  searchedValue: string;
  setSearchedValue: (e: string) => void;
  filters: IEventFilter;
  setFilters: (e: IEventFilter) => void;
  services: string[];
  setServices: (services: string[]) => void;
  handleService: (e: string) => void;
  locationService: ILocationService;
  setLocationService: (e: ILocationService) => void;
  privateSession: IPrivateSession;
  setPrivateSession: (e: IPrivateSession) => void;
  quantity: number;
  setQuantity: (e: number) => void;
}

const ScheduleEventContext = createContext<
  ScheduleEventContextType | undefined
>(undefined);

export const useScheduleEventContext = () => {
  const context = useContext(ScheduleEventContext);
  if (!context) {
    throw new Error(
      "useScheduleEventContent must be used within a ScheduleEventContentProvider"
    );
  }
  return context;
};

export type IEventFilter = {
  locations: string[];
  dateRange?: [Date, Date];
  isIndivdual?: boolean;
};

export const defaultEventFilters = {
  locations: [],
  dateRange: [
    new Date(new Date().setHours(0, 0, 0, 0)),
    new Date(new Date().setHours(23, 59, 59, 999)),
  ] as [Date, Date],
  isIndivdual: false,
};

export const ScheduleEventContentProvider = () => {
  const [searchedLocValue, setSearchedLocValue] = useDebouncedState("", 400);
  const [searchedValue, setSearchedValue] = useState("");
  const [filters, setFilters] = useState<IEventFilter>(defaultEventFilters);
  const [services, setServices] = useState<string[]>([]);
  const [locationService, setLocationService] =
    useState<ILocationService>(null);
  const [privateSession, setPrivateSession] = useState<IPrivateSession>(null);
  const [quantity, setQuantity] = useState(1);

  const handleService = (element: string) => {
    const index = services.indexOf(element);
    if (index === -1) {
      setServices([...services, element]);
    } else {
      setServices(services.filter((e) => e !== element));
    }
  };

  return (
    <ScheduleEventContext.Provider
      value={{
        searchedLocValue,
        setSearchedLocValue,
        searchedValue,
        setSearchedValue,
        filters,
        setFilters,
        services,
        setServices,
        handleService,
        locationService,
        setLocationService,
        privateSession,
        setPrivateSession,
        quantity,
        setQuantity,
      }}
    >
      <Outlet />
    </ScheduleEventContext.Provider>
  );
};
